import React from 'react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { ChevronRight, FileDown, TrendingDown, TrendingUp } from 'lucide-react';

/** Visit volume + doctor load table. */
export default function ManagementLoadSection({
  overview, workload, docs, rangeLabel, onOpenDoctors, downloadPdf,
}) {
  return (
    <div className="space-y-4" data-testid="mgmt-load-section">
      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Demand</div>
            <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Visit volume · {rangeLabel}
            </h3>
          </div>
          <div className="text-xs text-zinc-500">Asia/Kolkata</div>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={overview?.visits_per_day || []} margin={{ top: 5, right: 12, bottom: 12, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
              <Tooltip cursor={{ fill: '#eef2ff' }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="visits" fill="#F15A22" radius={[6, 6, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Load & capacity</div>
            <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Visits vs hours rostered
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={onOpenDoctors} data-testid="mgmt-doctors-drilldown">
            Full leaderboard <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm" data-testid="mgmt-doctors-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="text-left py-2">Doctor</th>
                <th className="text-left">Specialty</th>
                <th className="text-right">Visits</th>
                <th className="text-right">Hrs</th>
                <th className="text-right">Load</th>
                <th className="text-right">Queue</th>
                <th className="text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {docs.slice(0, 8).map((d, i) => (
                <tr key={d.doctor_id} className="border-t border-zinc-100">
                  <td className="py-2 font-semibold text-zinc-900">
                    Dr. {d.name}
                    {i === 0 && docs.length > 1 && (
                      <span className="ml-2 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5">HOT</span>
                    )}
                  </td>
                  <td className="text-zinc-600">{d.specialization || '—'}</td>
                  <td className="text-right font-mono">{d.visits}</td>
                  <td className="text-right font-mono">{d.hours_rostered.toFixed(1)}</td>
                  <td className="text-right font-mono font-semibold" style={{ color: d.loadIndex >= 4 ? '#be123c' : undefined }}>
                    {d.loadIndex.toFixed(1)}
                  </td>
                  <td className="text-right font-mono">{d.appointments_today}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf('doctor', d.doctor_id)}
                      className="text-blue-700" data-testid={`mgmt-doctor-pdf-${d.doctor_id}`}>
                      <FileDown className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!docs.length && (
                <tr><td colSpan={7} className="py-6 text-center text-zinc-400">No doctors yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {workload?.most_visits && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">
              <TrendingUp className="h-3 w-3" /> Most visits: <b>Dr. {workload.most_visits.name}</b>
            </span>
            {workload.least_visits && workload.doctors?.length > 1 && (
              <span className="inline-flex items-center gap-1 text-[11px] bg-zinc-50 text-zinc-700 border border-zinc-200 rounded-full px-3 py-1">
                <TrendingDown className="h-3 w-3" /> Lightest: <b>Dr. {workload.least_visits.name}</b>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
